import { ERROR_BOUNDARY_ERROR_MESSAGES } from './errorBoundaryMessages'

/**
 * Error message catalog flattened to array for matcher lookup.
 * @type {Array<object>}
 */
const boundaryMessageConfigs = Object.values(ERROR_BOUNDARY_ERROR_MESSAGES)

/**
 * Maps runtime error details to user-friendly boundary copy/action visibility.
 *
 * @param {unknown} error Caught runtime error value.
 * @returns {{ title: string, body: string, showReload: boolean, showLogout: boolean }}
 */
export const getFriendlyErrorCopy = (error) => {
  const errorName = String(error?.name || '')
  const errorMessage = String(error?.message || error || '')

  const matchedConfig = boundaryMessageConfigs.find((config) => {
    const matcher = config?.matcher || {}

    if (matcher.fallback) {
      return false
    }

    const nameMatched = Array.isArray(matcher.errorNames) && matcher.errorNames.includes(errorName)

    const messageMatched =
      Array.isArray(matcher.messagePatterns) &&
      matcher.messagePatterns.some((pattern) => {
        try {
          return new RegExp(pattern, 'i').test(errorMessage)
        } catch (regexError) {
          console.error('[ErrorBoundary] Invalid matcher regex pattern:', pattern, regexError)
          return false
        }
      })

    return nameMatched || messageMatched
  })

  if (matchedConfig) {
    return {
      title: matchedConfig.userMessage?.title || 'Oops! Something went wrong',
      body:
        matchedConfig.userMessage?.body ||
        'An unexpected error occurred. Please try reloading the page.',
      showReload: matchedConfig.actions?.showReload ?? true,
      showLogout: matchedConfig.actions?.showLogout ?? true,
    }
  }

  const fallbackConfig = boundaryMessageConfigs.find((config) => config?.matcher?.fallback)

  return {
    title: fallbackConfig?.userMessage?.title || 'Oops! Something went wrong',
    body:
      fallbackConfig?.userMessage?.body ||
      'An unexpected error occurred. Please try reloading the page.',
    showReload: fallbackConfig?.actions?.showReload ?? true,
    showLogout: fallbackConfig?.actions?.showLogout ?? true,
  }
}

/**
 * Chunk/lazy-load failures that show "We could not load part of the app" (not timeout/fallback).
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isChunkLoadRecoveryError = (error) => {
  const friendly = getFriendlyErrorCopy(error)
  return (
    friendly.title === 'We could not load part of the app' &&
    friendly.showReload &&
    !friendly.showLogout
  )
}
